/**
 * AI 어시스턴트 채팅 패널
 *
 * 화면 오른쪽 아래에 떠 있는 버튼을 누르면 열리는 사이드 패널이다.
 * 사용자가 "이번 달 미수 얼마야?" 처럼 말하면 AI 가 알아서 해당 SP를 찾아 조회하고 표로 정리해준다.
 *
 * 저장/수정/삭제 요청은 바로 실행하지 않는다. 안전장치가 두 겹이다:
 *   1) 채팅창에 "이런 값으로 저장할게요" 카드가 뜨고,
 *   2) [확인하고 실행] 을 누르면 팝업이 한 번 더 뜨며, 거기서 값을 고쳐 저장할 수 있다.
 * 서버로 나가는 건 팝업에서 [저장] 을 누른 뒤뿐이다.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, Link } from 'react-router-dom';
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { createSession, sendMessage, resolveWrite } from '../services/ai/aiChatService';
import { getRouteLabelWithId } from '../utils/routeLabel';
import { isAiConfigured } from '../services/ai/geminiClient';
import { resolveTarget } from '../services/ai/targetInfo';
import { isDemoMode } from '../demo/demoMode';
import {
  listBugTickets,
  submitBugReport,
  pollBugReplies,
} from '../services/ai/bugReportService';

/* ── 아주 작은 마크다운 렌더러 ────────────────────────────────
   표 / 굵게 / 목록 / 줄바꿈만 처리한다. 라이브러리를 새로 넣지 않기 위해 직접 그린다. */

/** 링크로 그려도 되는 주소인지. 앱 안 경로(/manuals/...)와 평범한 웹 주소만 허용한다. */
function safeHref(href) {
  const url = String(href || '').trim();
  if (url.startsWith('/')) return { url, internal: true };
  if (/^(https?:|mailto:)/i.test(url)) return { url, internal: false };
  return null; // javascript: 같은 것은 링크로 만들지 않는다
}

function renderInline(text, keyPrefix) {
  const nodes = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]\n]+\]\([^)\s]+\))/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('[')) {
      // 매뉴얼 링크 — 도우미가 근거로 삼은 장을 사용자가 바로 열어볼 수 있게 한다.
      const m = token.match(/^\[([^\]\n]+)\]\(([^)\s]+)\)$/);
      const target = m && safeHref(m[2]);
      if (!target) {
        nodes.push(token);
      } else if (target.internal) {
        nodes.push(
          <Link
            key={`${keyPrefix}-a${i}`}
            to={target.url}
            className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
          >
            {m[1]}
          </Link>
        );
      } else {
        nodes.push(
          <a
            key={`${keyPrefix}-a${i}`}
            href={target.url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
          >
            {m[1]}
          </a>
        );
      }
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={`${keyPrefix}-b${i}`} className="font-semibold text-gray-900">
          {token.slice(2, -2)}
        </strong>
      );
    } else {
      nodes.push(
        <code key={`${keyPrefix}-c${i}`} className="px-1 py-0.5 bg-gray-100 rounded text-[11px] text-gray-800">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const splitRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

function MarkdownLite({ text }) {
  const blocks = useMemo(() => {
    const lines = String(text || '').split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      const isTableHead = line.trim().startsWith('|') && /\|/.test(lines[i + 1] || '') && /^[\s|:-]+$/.test(lines[i + 1] || '');
      if (isTableHead) {
        const header = splitRow(line);
        const rows = [];
        i += 2;
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          rows.push(splitRow(lines[i]));
          i += 1;
        }
        out.push({ type: 'table', header, rows });
        continue;
      }
      if (/^\s*[-*]\s+/.test(line)) {
        const items = [];
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
          i += 1;
        }
        out.push({ type: 'list', items });
        continue;
      }
      out.push({ type: 'p', text: line });
      i += 1;
    }
    return out;
  }, [text]);

  return (
    <div className="space-y-2">
      {blocks.map((block, idx) => {
        if (block.type === 'table') {
          return (
            <div key={idx} className="overflow-x-auto -mx-1">
              <table className="min-w-full text-[11px] border border-gray-200 rounded">
                <thead className="bg-gray-50">
                  <tr>
                    {block.header.map((h, hi) => (
                      <th key={hi} className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri} className="even:bg-gray-50/60">
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-2 py-1 border-b border-gray-100 text-gray-700 whitespace-nowrap">
                          {renderInline(cell, `t${idx}-${ri}-${ci}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={idx} className="list-disc pl-4 space-y-0.5">
              {block.items.map((item, li) => (
                <li key={li}>{renderInline(item, `l${idx}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        if (!block.text.trim()) return <div key={idx} className="h-1" />;
        return (
          <p key={idx} className="leading-relaxed">
            {renderInline(block.text, `p${idx}`)}
          </p>
        );
      })}
    </div>
  );
}

/* ── 실행 확인 카드 ──────────────────────────────── */

function WriteConfirmCard({ pending, busy, onApprove, onDecline }) {
  const isDelete = /삭제|취소/.test(pending.preview.sp);
  return (
    <div className={`rounded-lg border p-3 text-xs ${isDelete ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className={`w-4 h-4 mt-0.5 shrink-0 ${isDelete ? 'text-red-500' : 'text-amber-500'}`} />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900">{pending.summary || '데이터를 변경하려고 합니다.'}</p>
          {/* 번호만 보여주면 대상이 맞는지 확인할 방법이 없다. 이름을 같이 띄운다. */}
          {pending.preview.target && (
            <div className="mt-1.5 rounded border border-gray-300 bg-white px-2 py-1.5">
              <p className="text-[10px] text-gray-500">이 {pending.preview.target.종류}에 저장합니다</p>
              <p className="text-[11px] font-semibold text-gray-900 break-all">
                {pending.preview.target.이름 || <span className="font-normal text-gray-400">(이름을 확인하지 못했습니다)</span>}
              </p>
              {pending.preview.target.부가 && (
                <p className="text-[10px] text-gray-700">{pending.preview.target.부가}</p>
              )}
              <p className="text-[10px] text-gray-500">
                {pending.preview.target.종류}번호 {pending.preview.target.번호}
              </p>
            </div>
          )}
          <p className="mt-1 text-[11px] text-gray-500 break-all">{pending.preview.sp}</p>
          <div className="mt-2 rounded border border-gray-200 bg-white overflow-hidden">
            <table className="w-full text-[11px]">
              <tbody>
                {pending.preview.params.map((p) => (
                  <tr key={p.name} className="border-b border-gray-100 last:border-0">
                    <td className="px-2 py-1 text-gray-500 whitespace-nowrap align-top w-28">{p.name}</td>
                    <td className="px-2 py-1 text-gray-800 break-all">
                      {String(p.value === '' ? '(빈 값)' : p.value)}
                      {p.auto && <span className="ml-1 text-[10px] text-gray-400">자동</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={onApprove}
              disabled={busy}
              className={`px-3 py-1.5 rounded-md text-white text-[11px] font-medium disabled:opacity-60 ${
                isDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {busy ? '실행 중…' : '확인하고 실행'}
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-[11px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              취소
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 실행 전 최종 확인 팝업 ────────────────────────────
 *
 * 카드에서 [확인하고 실행] 을 누르면 이 창이 뜬다. 안전장치가 두 겹인 이유:
 *   1) 채팅 카드는 좁아서 값이 눈에 잘 안 들어온다. 여기서 크게 한 번 더 본다.
 *   2) AI 가 값을 잘못 뽑았을 때(날짜를 잘못 잡거나 이름을 잘못 알아듣거나)
 *      취소하고 처음부터 다시 말하는 대신 그 자리에서 고쳐서 저장할 수 있다.
 *
 * 고칠 수 있는 것은 "사람 말로 된 값" 뿐이다. SP 파라미터 조립은 여전히 코드가 한다.
 * 아래 "실제로 서버에 보낼 값" 은 고칠 때마다 다시 계산해서 보여준다.
 */
function WriteConfirmModal({ pending, busy, onSave, onClose }) {
  const [values, setValues] = useState(() => {
    const init = {};
    (pending.fields || []).forEach((f) => { init[f.key] = f.value; });
    return init;
  });

  const isDelete = /삭제|취소/.test(pending.preview.sp);
  const hasFields = (pending.fields || []).length > 0;

  // 고친 값으로 다시 조립해 본다. 필수값이 비면 여기서 걸린다.
  const rebuilt = useMemo(() => {
    if (!pending.rebuild) return { preview: pending.preview };
    try {
      return pending.rebuild(values);
    } catch (error) {
      return { error: error?.message || String(error) };
    }
  }, [pending, values]);

  const preview = rebuilt.preview || pending.preview;
  const blocked = !!rebuilt.error;

  /* 대상(상품·예약·주문) 이름표.
   *
   * rebuild 는 동기라 그 안에서 서버를 부를 수 없다. 그래서 값이 바뀔 때마다 여기서 따로 받아온다.
   * 상품은 목록이 캐시돼 있어 즉시 나오고, 예약·주문은 번호를 바꿀 때마다 단건 조회가 한 번 나간다.
   * 늦게 도착한 응답이 최신 값을 덮지 않도록 alive 로 막는다. */
  const [target, setTarget] = useState(pending.preview.target || null);
  useEffect(() => {
    let alive = true;
    setTarget(preview.target || null); // 캐시에 있으면 먼저 보여주고
    resolveTarget(preview)
      .then((next) => { if (alive) setTarget(next); })
      .catch(() => {});
    return () => { alive = false; };
  }, [preview]);

  const changed = (pending.fields || []).some((f) => String(values[f.key] ?? '') !== String(f.value ?? ''));

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onMouseDown={busy ? undefined : onClose}>
      <div
        className="w-[min(560px,100%)] max-h-[85vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`px-4 py-3 border-b ${isDelete ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 shrink-0 ${isDelete ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">저장하기 전에 한 번 더 확인해 주세요</p>
              <p className="mt-0.5 text-xs text-gray-600">{pending.summary || '데이터를 변경하려고 합니다.'}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {/* 대상을 맨 위에 크게 둔다. 값이 맞는지보다 "어디에 저장하는지" 를 먼저 보게 하려는 것이다.
              preview 는 값을 고칠 때마다 다시 계산되므로 상품번호를 바꾸면 이름도 따라 바뀐다. */}
          {target && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
              <p className="text-[11px] text-blue-700">이 {target.종류}에 저장합니다</p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900 break-all">
                {target.이름 || <span className="font-normal text-gray-400">(이름을 확인하지 못했습니다 — 번호가 맞는지 직접 확인해 주세요)</span>}
              </p>
              {target.부가 && <p className="text-[11px] text-gray-700">{target.부가}</p>}
              <p className="text-[11px] text-gray-600">
                {target.종류}번호 {target.번호}
              </p>
            </div>
          )}

          {hasFields ? (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">
                값이 맞는지 보시고, 틀린 게 있으면 여기서 고치세요
              </p>
              <div className="space-y-2">
                {pending.fields.map((f) => (
                  <label key={f.key} className="block">
                    <span className="text-[11px] text-gray-600">
                      {f.key}
                      {f.required && <span className="ml-1 text-red-500">*</span>}
                      {f.isList && <span className="ml-1 text-gray-400">(쉼표로 구분)</span>}
                    </span>
                    <input
                      type="text"
                      value={values[f.key] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      disabled={busy}
                      className="mt-0.5 w-full px-2 py-1.5 rounded border border-gray-300 text-xs focus:outline-none focus:border-blue-400 disabled:bg-gray-100"
                    />
                    {f.desc && <span className="text-[10px] text-gray-400">{f.desc}</span>}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">고칠 수 있는 값이 없습니다. 아래 내용대로 실행됩니다.</p>
          )}

          <div>
            <p className="text-xs font-medium text-gray-700 mb-1">실제로 서버에 보낼 값</p>
            <p className="text-[10px] text-gray-400 mb-1 break-all">{preview.sp}</p>
            {blocked ? (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">{rebuilt.error}</div>
            ) : (
              <div className="rounded border border-gray-200 overflow-hidden">
                <table className="w-full text-[11px]">
                  <tbody>
                    {preview.params.map((p) => (
                      <tr key={p.name} className="border-b border-gray-100 last:border-0">
                        <td className="px-2 py-1 text-gray-500 whitespace-nowrap align-top w-32 bg-gray-50">{p.name}</td>
                        <td className="px-2 py-1 text-gray-800 break-all">
                          {String(p.value === '' ? '(빈 값)' : p.value)}
                          {p.auto && <span className="ml-1 text-[10px] text-gray-400">자동</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-500">
            {changed ? '값을 고쳤습니다. 고친 값으로 저장됩니다.' : 'AI 가 제안한 값 그대로 저장됩니다.'}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-xs border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              돌아가기
            </button>
            <button
              type="button"
              onClick={() => onSave(values)}
              disabled={busy || blocked}
              className={`px-4 py-1.5 rounded-md text-xs font-medium text-white disabled:opacity-60 ${
                isDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {busy ? '저장 중…' : isDelete ? '삭제 실행' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** 보관할 화면별 대화 개수. 넘으면 오래된 것부터 버린다. */
const MAX_CONVERSATIONS = 8;

/** 버그 신고 작성 팝업 */
function BugReportModal({ open, busy, initialText, onClose, onSubmit }) {
  const [text, setText] = useState(initialText || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setText(initialText || '');
      setError('');
    }
  }, [open, initialText]);

  if (!open) return null;

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setError('무엇을 신고할지 적어 주세요.');
      return;
    }
    setError('');
    try {
      await onSubmit(trimmed);
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onMouseDown={busy ? undefined : onClose}>
      <div
        className="w-[min(420px,100%)] rounded-xl bg-white shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <FlagIcon className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900">오류·버그 신고</p>
              <p className="mt-0.5 text-[11px] text-gray-600">
                화면·브라우저·최근 대화 일부를 함께 보냅니다. 담당자가 확인한 뒤 이 창에 답변을 남겨 드립니다.
              </p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 space-y-2">
          <label className="block">
            <span className="text-[11px] text-gray-600">무슨 일이 있었나요?</span>
            <textarea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={busy}
              placeholder="예: 미수금 조회했는데 표가 비어 나와요 / 입금 저장 버튼을 눌렀더니 오류가 나요"
              className="mt-1 w-full resize-none px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:bg-gray-100"
            />
          </label>
          {error && <p className="text-[11px] text-red-600">{error}</p>}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-xs border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !text.trim()}
            className="px-4 py-1.5 rounded-md text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
          >
            {busy ? '보내는 중…' : '신고 보내기'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── 본체 ──────────────────────────────── */

const AiChatPanel = () => {
  const location = useLocation();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  /**
   * 화면(경로)별로 대화를 따로 보관한다.
   *
   * 왜 나누나: 이 패널은 Layout 에 한 번 붙어 있어서 메뉴를 옮겨도 다시 만들어지지 않는다.
   * 그러니 가격/재고에서 한 얘기가 예약관리 화면까지 따라와 "아까 그거" 가 엉뚱한 걸
   * 가리키게 된다. 프롬프트(보는 화면·화면 전용 도구)는 매 턴 갱신되는데 대화만
   * 남아 있었기 때문에 더 헷갈렸다.
   *
   * 경로 전체를 열쇠로 쓴다. 같은 가격/재고라도 상품이 다르면 다른 대화다.
   * 되돌아오면 아까 대화가 그대로 있다(날리지 않는다).
   */
  const storeRef = useRef(new Map());
  const routeKey = location.pathname;
  const activeKeyRef = useRef(routeKey);
  const clockRef = useRef(0);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  // 팝업이 떠 있는지 (카드 → 팝업 → 저장 순서)
  const [confirming, setConfirming] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportSeed, setReportSeed] = useState('');
  const [bugTickets, setBugTickets] = useState(() => listBugTickets());

  const configured = isAiConfigured();
  const screenLabel = getRouteLabelWithId(routeKey);
  const ticketsWithReply = bugTickets.filter((t) => t.reply);
  const lastErrorText = [...messages].reverse().find((m) => m.error)?.text || '';

  /** 해당 화면의 대화를 꺼낸다. 없으면 새로 만든다. */
  const getEntry = useCallback((key) => {
    let entry = storeRef.current.get(key);
    if (!entry) {
      entry = { session: createSession(), messages: [], pending: null, busy: false };
      storeRef.current.set(key, entry);
    }
    clockRef.current += 1;
    entry.usedAt = clockRef.current;
    return entry;
  }, []);

  /**
   * 보관함이 무한정 커지지 않게 오래된 대화부터 버린다.
   * 지금 보고 있거나, 응답을 기다리는 중이거나, 승인 대기 중인 대화는 건드리지 않는다.
   */
  const pruneStore = useCallback(() => {
    const store = storeRef.current;
    if (store.size <= MAX_CONVERSATIONS) return;
    const victims = [...store.entries()]
      .filter(([key, entry]) => key !== activeKeyRef.current && !entry.busy && !entry.pending)
      .sort((a, b) => (a[1].usedAt || 0) - (b[1].usedAt || 0));
    while (store.size > MAX_CONVERSATIONS && victims.length) {
      store.delete(victims.shift()[0]);
    }
  }, []);

  /**
   * 보관함에 먼저 쓰고, 그 화면을 지금 보고 있을 때만 화면을 갱신한다.
   * 답변을 기다리는 도중 다른 메뉴로 옮겨도 결과가 엉뚱한 대화에 붙지 않는다.
   */
  const append = useCallback(
    (message, key = activeKeyRef.current) => {
      const entry = getEntry(key);
      entry.messages = [...entry.messages, { id: `${Date.now()}-${Math.random()}`, ...message }];
      if (key === activeKeyRef.current) setMessages(entry.messages);
    },
    [getEntry]
  );

  const setPendingFor = useCallback(
    (key, value) => {
      getEntry(key).pending = value;
      if (key === activeKeyRef.current) setPending(value);
    },
    [getEntry]
  );

  const setBusyFor = useCallback(
    (key, value) => {
      getEntry(key).busy = value;
      if (key === activeKeyRef.current) setBusy(value);
    },
    [getEntry]
  );

  /** 화면이 바뀌면 그 화면의 대화로 갈아끼운다 */
  useLayoutEffect(() => {
    if (activeKeyRef.current === routeKey) return;
    activeKeyRef.current = routeKey;
    const entry = getEntry(routeKey);
    setMessages(entry.messages);
    setPending(entry.pending);
    setBusy(entry.busy);
    setConfirming(false);
    pruneStore();
  }, [routeKey, getEntry, pruneStore]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending, busy, ticketsWithReply.length]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  /** 패널이 열려 있고 답변 대기 티켓이 있으면 가끔 시트에서 답변을 읽어 온다. */
  useEffect(() => {
    if (!isOpen || !configured) return undefined;

    let cancelled = false;
    const refresh = async () => {
      try {
        await pollBugReplies();
      } catch {
        /* ignore */
      }
      if (!cancelled) setBugTickets(listBugTickets());
    };

    refresh();
    const timer = setInterval(refresh, 45000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOpen, configured]);

  const openReport = useCallback((seed = '') => {
    setReportSeed(seed);
    setReportOpen(true);
  }, []);

  const handleBugSubmit = useCallback(
    async (description) => {
      setReportBusy(true);
      try {
        const key = activeKeyRef.current;
        const { ticketId } = await submitBugReport({
          description,
          route: location.pathname,
          screenLabel,
          messages: getEntry(key).messages,
          lastError: lastErrorText,
        });
        setBugTickets(listBugTickets());
        append(
          {
            role: 'assistant',
            text: `신고를 접수했습니다.\n\n접수번호 **${ticketId}**\n담당자가 확인하면 이 창에 답변이 표시됩니다.`,
          },
          key
        );
        setReportOpen(false);
      } finally {
        setReportBusy(false);
      }
    },
    [append, getEntry, lastErrorText, location.pathname, screenLabel]
  );

  /** SP 호출 진행 상황을 대화창에 한 줄씩 남긴다 (무슨 일이 벌어지는지 보이게) */
  const makeEventHandler = useCallback(
    (key) => (event) => {
      if (event.type === 'tool-start') {
        const label =
          event.name === 'search_procedures'
            ? `'${event.args?.keyword || ''}' 관련 기능을 찾는 중…`
            : `${String(event.args?.procedureName || event.name || '').replace(/^DBO\./, '')} 실행 중…`;
        append({ role: 'tool', text: label }, key);
      } else if (event.type === 'tool-end' && event.result?.오류) {
        append({ role: 'tool', text: `실패: ${event.result.오류}`, error: true }, key);
      } else if (event.type === 'tool-end' && typeof event.result?.rowCount === 'number') {
        append({ role: 'tool', text: `${event.result.rowCount}건 조회됨` }, key);
      }
    },
    [append]
  );

  const runTurn = useCallback(
    async (key, starter) => {
      setBusyFor(key, true);
      setPendingFor(key, null);
      try {
        const result = await starter();
        if (result.status === 'confirm') {
          setPendingFor(key, result.pending);
          if (result.text) append({ role: 'assistant', text: result.text }, key);
        } else if (result.text) {
          append({ role: 'assistant', text: result.text }, key);
        }
      } catch (error) {
        append({ role: 'assistant', text: `오류가 났습니다.\n\n${error?.message || error}`, error: true }, key);
      } finally {
        setBusyFor(key, false);
      }
    },
    [append, setBusyFor, setPendingFor]
  );

  const handleSend = useCallback(
    (text) => {
      const trimmed = String(text || '').trim();
      if (!trimmed || busy) return;
      const key = activeKeyRef.current;
      const route = location.pathname;
      append({ role: 'user', text: trimmed }, key);
      setInput('');
      runTurn(key, () =>
        sendMessage(getEntry(key).session, trimmed, {
          onEvent: makeEventHandler(key),
          route,
        })
      );
    },
    [append, busy, getEntry, location.pathname, makeEventHandler, runTurn]
  );

  /**
   * 확인 절차의 마지막 단계.
   * approved=true 로 올 때는 팝업에서 [저장] 을 누른 것이고, values 에 사용자가 고친 값이 들어 있다.
   */
  const handleResolve = useCallback(
    (approved, values) => {
      const target = pending;
      if (!target) return;
      const key = activeKeyRef.current;
      const route = location.pathname;
      const edited =
        approved && values && (target.fields || []).some((f) => String(values[f.key] ?? '') !== String(f.value ?? ''));
      append(
        {
          role: 'tool',
          text: approved
            ? `${edited ? '값을 고쳐서 실행' : '실행 승인'}: ${target.summary || target.preview.sp}`
            : '실행을 취소했습니다.',
        },
        key
      );
      setConfirming(false);
      runTurn(key, () =>
        resolveWrite(getEntry(key).session, approved, {
          onEvent: makeEventHandler(key),
          route,
          ...(approved && values ? { values } : {}),
        })
      );
    },
    [append, getEntry, location.pathname, makeEventHandler, pending, runTurn]
  );

  /** 지금 보고 있는 화면의 대화만 새로 시작한다. 다른 화면 대화는 그대로 둔다. */
  const handleReset = useCallback(() => {
    const entry = getEntry(activeKeyRef.current);
    entry.session = createSession();
    entry.messages = [];
    entry.pending = null;
    setMessages([]);
    setPending(null);
    setConfirming(false);
  }, [getEntry]);

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition"
          title="AI 어시스턴트"
        >
          <SparklesIcon className="w-5 h-5" />
          <span className="text-sm font-medium">AI 어시스턴트</span>
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 z-40 w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-6rem))] flex flex-col rounded-xl shadow-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 min-w-0">
              <SparklesIcon className="w-4 h-4 text-blue-600 shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-gray-800">AI 어시스턴트</span>
                  {isDemoMode() && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 text-purple-700">데모</span>
                  )}
                </div>
                {/* 대화는 화면마다 따로 보관된다. 지금 보고 있는 게 어느 화면 대화인지 알려준다. */}
                <div className="text-[10px] text-gray-500 truncate" title={routeKey}>
                  {screenLabel} 대화
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {configured && (
                <button
                  type="button"
                  onClick={() => openReport(lastErrorText ? `오류 내용:\n${lastErrorText}` : '')}
                  className="p-1.5 rounded hover:bg-amber-100 text-amber-600"
                  title="오류·버그 신고"
                >
                  <FlagIcon className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={handleReset}
                className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                title="이 화면 대화 새로 시작"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                title="닫기"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-xs text-gray-700">
            {ticketsWithReply.length > 0 && (
              <div className="space-y-2">
                {ticketsWithReply.slice(0, 5).map((t) => (
                  <div key={t.id} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <div className="flex items-center justify-between gap-2 text-[10px] text-emerald-700">
                      <span className="font-medium">개발자 답변 · {t.id}</span>
                      {t.repliedAt && (
                        <span className="text-emerald-600/80 shrink-0">
                          {new Date(t.repliedAt).toLocaleString('ko-KR', {
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <p className="mt-1 text-[10px] text-emerald-800/70 line-clamp-2">신고: {t.description}</p>
                    )}
                    <p className="mt-1 text-[11px] text-gray-800 whitespace-pre-wrap break-words">{t.reply}</p>
                  </div>
                ))}
              </div>
            )}

            {/*
              연결 설정이 없을 때. 개발자와 사용자에게 다른 것을 보여준다 —
              쓰는 분에게 ".env 에 넣고 dev 서버를 재시작하세요" 는 할 수 있는 일이 아니고,
              내부 파일 이름을 굳이 알릴 이유도 없다. 원인을 아는 사람은 개발자 쪽이다.
            */}
            {!configured && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[11px] text-red-700">
                {process.env.NODE_ENV === 'production' ? (
                  <>
                    AI 어시스턴트가 아직 연결되지 않았습니다. 잠시 후 다시 열어보시고,
                    계속 이 화면이 보이면 담당자에게 알려주세요.
                  </>
                ) : (
                  <>
                    AI 연결 설정이 없습니다. 프로젝트 루트 <code>.env</code> 에
                    <code className="mx-1">REACT_APP_SUPABASE_URL</code>
                    <code className="mr-1">REACT_APP_SUPABASE_ANON_KEY</code>
                    <code className="mr-1">REACT_APP_ERP_APP_TOKEN</code>
                    을 넣고 개발 서버를 다시 시작하세요.
                    (Gemini 키는 브라우저가 아니라 Supabase 시크릿에 들어갑니다 — <code>supabase/functions/README.md</code>)
                    <br />
                    ⚠ 운영 빌드에서 이 화면이 보인다면 <b>빌드한 곳에 .env 가 없었던 것</b>입니다.
                    CRA 는 빌드 시점에 값을 번들에 박으므로, 배포 후 서버에 .env 를 넣어도 반영되지 않습니다.
                  </>
                )}
              </div>
            )}

            {messages.length === 0 && configured && (
              <div className="leading-relaxed space-y-3">
                <p className="text-gray-600">무엇이 궁금하세요? 말하듯 편하게 물어보시면 됩니다.</p>

                {/* 설명보다 예시가 낫다 — "무엇을 물어봐도 되는지" 를 아는 게 첫 문턱이라서. */}
                <ul className="space-y-2">
                  <li>
                    <div className="text-gray-700 font-medium">찾아드려요</div>
                    <div className="text-gray-400">“이번 달 미수금 얼마나 남았어?”</div>
                  </li>
                  <li>
                    <div className="text-gray-700 font-medium">대신 처리해드려요</div>
                    <div className="text-gray-400">“이 예약 50만원 입금 처리해줘”</div>
                  </li>
                  <li>
                    <div className="text-gray-700 font-medium">사용법도 알려드려요</div>
                    <div className="text-gray-400">“재고구분이랑 요금제가 뭐가 달라?”</div>
                  </li>
                </ul>

                <p className="text-[11px] text-gray-400">
                  등록·수정은 실행하기 전에 확인 카드를 먼저 보여드리니 편하게 말씀하세요.
                  <br />
                  대화는 화면마다 따로 담아둡니다. 다른 메뉴에 갔다 오셔도 이 화면 대화는 그대로 이어집니다.
                </p>

                <button
                  type="button"
                  onClick={() => openReport('')}
                  className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 hover:text-amber-800"
                >
                  <FlagIcon className="w-3.5 h-3.5" />
                  오류·버그가 있으면 여기서 신고해 주세요
                </button>
              </div>
            )}

            {messages.map((message) => {
              if (message.role === 'user') {
                return (
                  <div key={message.id} className="flex justify-end">
                    <div className="max-w-[85%] px-3 py-2 rounded-2xl rounded-br-sm bg-blue-600 text-white whitespace-pre-wrap break-words">
                      {message.text}
                    </div>
                  </div>
                );
              }
              if (message.role === 'tool') {
                return (
                  <div
                    key={message.id}
                    className={`flex items-center gap-1.5 text-[11px] ${message.error ? 'text-red-500' : 'text-gray-400'}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current shrink-0" />
                    <span className="break-all">{message.text}</span>
                  </div>
                );
              }
              return (
                <div key={message.id} className="flex justify-start">
                  <div
                    className={`max-w-[92%] px-3 py-2 rounded-2xl rounded-bl-sm border ${
                      message.error ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <MarkdownLite text={message.text} />
                    {message.error && configured && (
                      <button
                        type="button"
                        onClick={() => openReport(`오류 내용:\n${message.text}`)}
                        className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-700 hover:text-amber-800"
                      >
                        <FlagIcon className="w-3 h-3" />
                        이 오류 신고하기
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {pending && (
              <WriteConfirmCard
                pending={pending}
                busy={busy}
                onApprove={() => setConfirming(true)}
                onDecline={() => handleResolve(false)}
              />
            )}

            {busy && !pending && (
              <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <ArrowPathIcon className="w-3 h-3 animate-spin" />
                생각하는 중…
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 p-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(input);
                  }
                }}
                placeholder={configured ? '무엇을 도와드릴까요? (Shift+Enter 줄바꿈)' : 'API 키 설정이 필요합니다'}
                disabled={!configured || busy}
                className="flex-1 resize-none max-h-28 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
              <button
                type="button"
                onClick={() => handleSend(input)}
                disabled={!configured || busy || !input.trim()}
                className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                title="보내기"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 실행 직전 최종 확인 — 채팅창 밖(document.body)에 그려서 좁은 패널에 갇히지 않게 한다 */}
      {confirming && pending && (
        <WriteConfirmModal
          pending={pending}
          busy={busy}
          onSave={(values) => handleResolve(true, values)}
          onClose={() => setConfirming(false)}
        />
      )}

      <BugReportModal
        open={reportOpen}
        busy={reportBusy}
        initialText={reportSeed}
        onClose={() => !reportBusy && setReportOpen(false)}
        onSubmit={handleBugSubmit}
      />
    </>
  );
};

export default AiChatPanel;
